import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Hand, Power, Battery, Moon, Sun, Mic, MessageCircle, Music, Clock, Heart } from 'lucide-react'

const BasicOperations = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-8 transition-colors duration-200 group">
          <ArrowLeft className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          返回主页
        </Link>
        
        <div className="mb-16">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl mb-8 shadow-2xl">
            <Hand className="text-white w-12 h-12" />
          </div>
          <h1 className="text-6xl md:text-7xl font-bold text-slate-900 mb-6 tracking-tight leading-tight">
            基础操作与指令参考
          </h1>
          <p className="text-2xl text-slate-600 max-w-3xl leading-relaxed">
            设备操作与指令，轻松上手
          </p>
        </div>
        
        <div className="space-y-8">
          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Hand className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">触摸反馈</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-slate-50 rounded-2xl p-8 overflow-x-auto mb-8">
                <table className="min-w-full border-collapse border border-slate-200">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="border border-slate-200 px-6 py-4 text-left text-base font-semibold text-slate-900">动作</th>
                      <th className="border border-slate-200 px-6 py-4 text-left text-base font-semibold text-slate-900">表情</th>
                      <th className="border border-slate-200 px-6 py-4 text-left text-base font-semibold text-slate-900">声音</th>
                      <th className="border border-slate-200 px-6 py-4 text-left text-base font-semibold text-slate-900">音频内容</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white hover:bg-slate-50 transition-colors">
                      <td className="border border-slate-200 px-6 py-4 text-base">跌落与拍打身体</td>
                      <td className="border border-slate-200 px-6 py-4 text-base">难过</td>
                      <td className="border border-slate-200 px-6 py-4 text-base">哭的声音</td>
                      <td className="border border-slate-200 px-6 py-4 text-base">啊，好疼</td>
                    </tr>
                    <tr className="bg-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="border border-slate-200 px-6 py-4 text-base">摇晃身体</td>
                      <td className="border border-slate-200 px-6 py-4 text-base">眩晕</td>
                      <td className="border border-slate-200 px-6 py-4 text-base">眩晕的声音</td>
                      <td className="border border-slate-200 px-6 py-4 text-base">好晕啊</td>
                    </tr>
                    <tr className="bg-white hover:bg-slate-50 transition-colors">
                      <td className="border border-slate-200 px-6 py-4 text-base">触摸头部1</td>
                      <td className="border border-slate-200 px-6 py-4 text-base">开心</td>
                      <td className="border border-slate-200 px-6 py-4 text-base">舒服的声音</td>
                      <td className="border border-slate-200 px-6 py-4 text-base">好舒服啊</td>
                    </tr>
                    <tr className="bg-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="border border-slate-200 px-6 py-4 text-base">触摸头部2</td>
                      <td className="border border-slate-200 px-6 py-4 text-base">喜爱</td>
                      <td className="border border-slate-200 px-6 py-4 text-base">欢快的声音</td>
                      <td className="border border-slate-200 px-6 py-4 text-base">呵呵</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="bg-slate-50 rounded-2xl p-6">
                  <img 
                    src="/eyes-sad.png" 
                    alt="难过效果" 
                    className="w-full rounded-xl shadow-lg mb-4"
                  />
                  <p className="text-base text-center text-slate-600 font-medium">难过效果</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-6">
                  <img 
                    src="/eyes-dizzy.png" 
                    alt="眩晕效果" 
                    className="w-full rounded-xl shadow-lg mb-4"
                  />
                  <p className="text-base text-center text-slate-600 font-medium">眩晕效果</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-6">
                  <img 
                    src="/eyes-happy.png" 
                    alt="开心效果" 
                    className="w-full rounded-xl shadow-lg mb-4"
                  />
                  <p className="text-base text-center text-slate-600 font-medium">开心效果</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-6">
                  <img 
                    src="/eyes-like.png" 
                    alt="喜爱效果" 
                    className="w-full rounded-xl shadow-lg mb-4"
                  />
                  <p className="text-base text-center text-slate-600 font-medium">喜爱效果</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Power className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">开关机</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-slate-50 rounded-2xl p-8">
                <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                  <li><strong className="text-violet-700">开机：</strong>长按玩偶左手 3 秒，指示灯亮起，语音提示"正在连接网络"，自动联网</li>
                  <li><strong className="text-violet-700">关机：</strong>开机状态长按长按玩偶左手 3 秒，语音提示"正在关机"，设备完全断电</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Battery className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">充电操作</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-slate-50 rounded-2xl p-8">
                <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                  <li>电量灯闪烁代表低电量，将 Type-C 线连接 5V 充电器插入充电口</li>
                  <li>充电中电量灯持续闪烁；充满后电量灯常亮</li>
                  <li><strong className="text-violet-700">重要提示：</strong>充电全程设备无法语音对话，充满后拔线恢复功能</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Moon className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">待机 / 休眠切换</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-slate-50 rounded-2xl p-8">
                <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                  <li><strong className="text-violet-700">自然待机模式：</strong>持续 60 秒无语音交互，播报「我先退下了，有事再叫我」进入待机</li>
                  <li><strong className="text-violet-700">深度休眠模式：</strong>长按设备右手，设备播放："我先去休息了，有事请按键叫我呀"。进入深度休眠，降低功耗</li>
                  <li>进入待机状态后超过 180 秒不唤醒设备，设备会进入深度休眠状态</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Sun className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">唤醒设备</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-slate-50 rounded-2xl p-8">
                <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                  <li>自然待机模式下，说出设置好的专属唤醒词，听到应答提示音即可开始语音交互</li>
                  <li>自然待机和深度休眠模式下，短按玩偶右手，设备答复："你好，有什么能帮助你的吗？"， 开启聊天模式</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Mic className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">打断播报</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-slate-50 rounded-2xl p-8">
                <p className="text-lg leading-relaxed mb-0">播报音乐、故事、评测内容过程中，再次呼叫唤醒词，可立即停止当前内容，开启新对话</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-500 to-gray-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Moon className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">退出对话</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-slate-50 rounded-2xl p-8">
                <p className="text-lg leading-relaxed mb-0">无语音交互 60 秒自动结束对话进入待机；也可语音说 "休息一下" 主动退出</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
                <MessageCircle className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">高频语音指令参考</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-8 border border-violet-100">
                  <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                    <MessageCircle className="w-6 h-6 text-violet-600" />
                    聊天对话类
                  </h3>
                  <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                    <li>陪我聊会儿天</li>
                    <li>给我讲长篇小故事</li>
                    <li>记住我刚刚说的话，等下提问你</li>
                  </ul>
                </div>
                
                <div className="bg-slate-50 rounded-2xl p-8">
                  <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                    <Music className="w-6 h-6 text-slate-600" />
                    音乐相关
                  </h3>
                  <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                    <li>推荐适合现在天气听的歌</li>
                    <li>生成一段"气球"相关音乐</li>
                    <li>你觉得我唱的怎么样</li>
                  </ul>
                </div>
                
                <div className="bg-slate-50 rounded-2xl p-8">
                  <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                    <Clock className="w-6 h-6 text-slate-600" />
                    定时闹钟
                  </h3>
                  <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                    <li>设置每天晚上 8 点提醒我</li>
                    <li>取消所有闹钟</li>
                    <li>现在几点，今天天气怎么样</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 border border-pink-100">
                  <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                    <Heart className="w-6 h-6 text-pink-600" />
                    情绪倾诉
                  </h3>
                  <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                    <li>我今天很不开心，安慰我一下</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BasicOperations
